type DroppedFiles = {
  files: File[];
  /** Dropped folders, snapshotted synchronously; expand them with `readDirectoryFiles`. */
  directoryEntries: FileSystemDirectoryEntry[];
  /** Folder-shaped item with no entry API (clipboard placeholder) — not traversable. */
  hasUnreadableDirectory: boolean;
};

export const MAX_ATTACHMENT_FILES = 10;

// A folder surfaces as a zero-byte File with no MIME type. `webkitGetAsEntry()`
// is the reliable signal for drag-and-drop, but it's null on the clipboard (it's
// backed by the drag data store), so fall back to the placeholder's shape there.
const looksLikeDirectory = (file: File) => file.size === 0 && file.type === '';

// Works for both drag-and-drop (`event.dataTransfer`) and paste (`event.clipboardData`).
// Must run synchronously inside the event handler: the DataTransferItemList is
// neutered once the handler yields, but the returned directory entries survive it.
export const getDroppedFiles = (dataTransfer: Pick<DataTransfer, 'files' | 'items'>): DroppedFiles => {
  const result: DroppedFiles = { files: [], directoryEntries: [], hasUnreadableDirectory: false };
  const addFileWithoutEntry = (file: File) => {
    if (looksLikeDirectory(file)) result.hasUnreadableDirectory = true;
    else result.files.push(file);
  };

  const items = Array.from(dataTransfer.items);
  if (items.length === 0) {
    for (const file of Array.from(dataTransfer.files)) addFileWithoutEntry(file);
    return result;
  }

  for (const item of items) {
    if (item.kind !== 'file') continue;
    const entry = item.webkitGetAsEntry?.();
    const file = item.getAsFile();
    if (entry?.isDirectory) result.directoryEntries.push(entry as FileSystemDirectoryEntry);
    else if (entry && file) result.files.push(file);
    else if (file) addFileWithoutEntry(file);
  }

  return result;
};

const isHiddenEntry = (name: string) => name.startsWith('.');

// A folder picked via `<input webkitdirectory>` arrives flat: each File's
// webkitRelativePath ("folder/sub/x.txt") is the only trace of the tree. Mirror
// the drop path's rules: the root segment is exempt (it's the folder the user
// deliberately picked), every deeper segment — including the basename — is
// checked for hiddenness.
export const filterHiddenPickedFiles = (files: FileList | File[]): File[] =>
  Array.from(files).filter(file => {
    const path = file.webkitRelativePath || file.name;
    return !path.split('/').slice(1).some(isHiddenEntry);
  });

// Resolves null for an unreadable file — one broken file shouldn't sink its siblings.
const readFileEntry = (entry: FileSystemFileEntry): Promise<File | null> =>
  new Promise(resolve => entry.file(resolve, () => resolve(null)));

const readEntriesBatch = (reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> =>
  new Promise((resolve, reject) => reader.readEntries(resolve, reject));

// readEntries returns results in batches (Chrome caps them at 100), so drain the
// reader until it comes back empty.
const readAllChildren = async (dir: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> => {
  const reader = dir.createReader();
  const children: FileSystemEntry[] = [];
  let batch = await readEntriesBatch(reader);
  while (batch.length > 0) {
    children.push(...batch);
    batch = await readEntriesBatch(reader);
  }
  return children;
};

const tooManyFilesError = new Error('folder holds too many files');

const collectFiles = async (entry: FileSystemEntry, files: File[]): Promise<void> => {
  if (entry.isFile) {
    if (files.length >= MAX_ATTACHMENT_FILES) throw tooManyFilesError;
    const file = await readFileEntry(entry as FileSystemFileEntry);
    if (file) files.push(file);
    return;
  }
  // A directory that fails to enumerate is skipped; its siblings still attach.
  const children = await readAllChildren(entry as FileSystemDirectoryEntry).catch(() => []);
  // Hidden entries are skipped as directories too, so a dropped project never
  // recurses into `.git` and friends.
  const visible = children.filter(child => !isHiddenEntry(child.name));
  for (const child of visible) {
    await collectFiles(child, files);
  }
};

// Flattens dropped folders into their individual files. A tree holding more than
// MAX_ATTACHMENT_FILES files is refused outright (empty `files` + `tooManyFiles`) —
// never truncated to an arbitrary traversal-order subset; the walk aborts as soon
// as the cap is crossed.
export const readDirectoryFiles = async (
  entries: FileSystemDirectoryEntry[],
): Promise<{ files: File[]; tooManyFiles: boolean }> => {
  const files: File[] = [];
  try {
    for (const entry of entries) {
      await collectFiles(entry, files);
    }
  } catch (err) {
    if (err !== tooManyFilesError) throw err;
    return { files: [], tooManyFiles: true };
  }
  return { files, tooManyFiles: false };
};
